import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import cloneDeep from 'lodash/cloneDeep'
import capitalize from 'lodash/capitalize'

import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'

import { Box, Button, DialogContent, Divider, FormControlLabel, List, Typography, useMediaQuery, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import AddIcon from '@mui/icons-material/Add'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { combineAddressInfo, getAddressParams } from 'utils/helper'
import { getActiveDeactivatedLocations } from 'utils/location'
import { CONTACT_METHOD_TYPES, CONTACT_METHODS } from 'settings/constants/contacts'
import { fixBeneficialOwnerContactLocationAssociation } from 'utils/form-body'
import { HHFormCheckboxField, HHFormTextField } from 'components/form-fields/v5'
import { useLazyGetAccountDetailsQuery } from 'api/accounts/getAccountDetails'
import { putIsLoading } from 'middleware/actions/response'

import T from 'T'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import HHFormSwitchIconField from 'components/form-fields/v5/HHFormSwitchIconField'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import HHFormMultiSelectAutocompleteField from 'components/form-fields/v5/HHFormMultiSelectAutocompleteField'
import AddressRowOne from 'components/common/address/AddressRowOne'
import AddressRowTwo from 'components/common/address/AddressRowTwo'
import { noop } from 'lodash'
import { useCreateBeneficialOwnerContactMutation, useUpdateBeneficialOwnerContactMutation } from 'api/contact/beneficialOwnersCrud'
import BeneficialOwnerContactInfo from 'components/customer-details/content/beneficial_owner_contacts/BeneficialOwnerContactInfo'
import { INITIAL_STATE } from './settings'

const { EMAIL, FAX } = CONTACT_METHODS
const { PERSONAL } = CONTACT_METHOD_TYPES

export const BeneficialOwnerContactInfoModel = (method = '') =>
  cloneDeep({
    type: PERSONAL,
    method,
    data: {
      value: '',
      phoneExtension: '',
    },
    serviceNotifications: method !== FAX,
    billingNotifications: method === EMAIL,
  })

const AddEditBeneficialOwnerDialog = ({ isOpen = false, accountId, contact, onClose }) => {
  const [getAccountDetails, { data: customerDetails }] = useLazyGetAccountDetailsQuery()
  const dispatch = useDispatch()
  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'))
  const contactId = get(contact, 'id', false)
  const { activeLocations } = getActiveDeactivatedLocations(customerDetails)
  const serviceLocations = activeLocations.map(location => ({ id: location.id, label: combineAddressInfo(get(location, 'address', {})) }))

  const [createBeneficialOwnerMutation, { isLoading: isCreateBeneficialOwnerLoading }] = useCreateBeneficialOwnerContactMutation()
  const [updateBeneificialOwnerMutation, { isLoading: isUpdateBeneficialOwnerLoading }] = useUpdateBeneficialOwnerContactMutation()
  const loading = useMemo(
    () => isCreateBeneficialOwnerLoading || isUpdateBeneficialOwnerLoading,
    [isCreateBeneficialOwnerLoading, isUpdateBeneficialOwnerLoading]
  )
  const initialState = contact || INITIAL_STATE
  const [expandedContactMethods, setExpandedContactMethods] = useState([0])
  const contactForm = useForm({ defaultValues: { ...initialState, active: !initialState?.deactivated } })
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setFocus,
    clearErrors,
    reset,
    formState: { errors },
  } = contactForm

  const { address: addressErrors } = errors

  const { append: appendContactMethod, remove: removeContactMethod } = useFieldArray({
    control,
    name: 'contactInfos',
  })

  const watchLine1 = watch('address.line1')
  const watchCity = watch('address.city')
  const watchState = watch('address.state')
  const watchZipCode = watch('address.zipCode')

  const handleAddressChange = address => {
    const { line1, city, state, zipCode, country, longitudeFixed, latitudeFixed } = getAddressParams(address)
    setValue('address.line1', line1)
    setValue('address.city', city)
    setValue('address.state', state)
    setValue('address.country', country)
    setValue('address.zipCode', zipCode)
    setValue('address.latitude', latitudeFixed)
    setValue('address.longitude', longitudeFixed)
  }

  const contactInfos = watch('contactInfos')

  const handleAddContactMethod = () => {
    appendContactMethod({ ...BeneficialOwnerContactInfoModel(EMAIL) })
    setExpandedContactMethods([contactInfos.length])
  }

  const onSubmitHandler = data => {
    // Don't save empty contact methods
    const removeEmptyContactMethods = get(data, 'contactInfos').filter(info => info?.data?.value)
    const fixContactsAssociation = fixBeneficialOwnerContactLocationAssociation([{ ...data, contactInfos: removeEmptyContactMethods }])
    const payload = { accountId, id: contactId, ...get(fixContactsAssociation, '[0]', {}) }
    if (contactId) {
      updateBeneificialOwnerMutation(payload)
        .unwrap()
        .then(() => {
          onClose()
        })
        .catch(handleError)
    } else {
      createBeneficialOwnerMutation(payload)
        .unwrap()
        .then(() => {
          onClose()
          toast.success(contact ? T.CONTACT_UPDATED_SUCCESS : T.CONTACT_ADDED_SUCCESS)
        })
        .catch(handleError)
    }
  }

  const onSubmitErrorHandler = err => {
    toast.error(T.REQUIRED_FIELD_MSG)
    const errorMethods = Object.keys(get(err, 'contactInfos', []))
    if (errorMethods.length) {
      setExpandedContactMethods(errorMethods.map(errorIndex => parseInt(errorIndex, 10)))
    }
  }

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name && Array.isArray(name.split('.'))) {
        const [field] = name.split('.')
        if (field === 'associatedWithLocation') {
          setValue('locations', [])
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [customerDetails, watch])

  useEffect(() => {
    if (isOpen && accountId && setFocus) {
      dispatch(putIsLoading(true))
      getAccountDetails({ accountId })
        .unwrap()
        .then(fetchedCustomerDetails => {
          if (contact) {
            const { activeLocations: fetchedActiveLocations } = getActiveDeactivatedLocations(fetchedCustomerDetails)
            const fetchedServiceLocations = fetchedActiveLocations.map(location => ({
              id: location.id,
              label: combineAddressInfo(get(location, 'address', {})),
            }))

            const mappedLocations = (contact.locations || [])
              .map(locationId => fetchedServiceLocations.find(loc => loc.id === locationId))
              .filter(Boolean)

            const formValues = {
              ...contact,
              active: !contact?.deactivated,
              locations: mappedLocations,
              associatedWithLocation: mappedLocations.length > 0,
            }

            reset(formValues)
          }
        })
        .catch(handleError)
        .finally(() => dispatch(putIsLoading(false)))
      setTimeout(() => setFocus('firstName'), 100)
      clearErrors()
    }
  }, [isOpen, accountId, setFocus])

  return (
    <>
      <HHBaseDialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
        <HHDialogTitle title={contact ? 'Create beneficial owner contact' : 'Edit beneficial owner contact'} onClose={onClose} />
        <DialogContent>
          <Grid container mt={2} columnGap={3}>
            <Grid
              container
              rowGap={1}
              spacing={1}
              height="fit-content"
              size={{
                xs: 12,
                md: 5,
              }}
            >
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <HHFormTextField
                  control={control}
                  deprecatedLabel={false}
                  error={errors?.firstName}
                  fullWidth
                  label={capitalize(T.FIRST_NAME)}
                  name="firstName"
                  required
                  rules={{ required: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <HHFormTextField control={control} name="lastName" label={capitalize(T.LAST_NAME)} fullWidth deprecatedLabel={false} />
              </Grid>
              <Grid size={12}>
                <HHFormTextField control={control} name="relationship" label={T.TITLE} fullWidth deprecatedLabel={false} />
              </Grid>
              <Grid size={12}>
                <HHFormTextField control={control} name="businessName" label={T.BUSINESS_NAME} fullWidth deprecatedLabel={false} />
              </Grid>
              <Grid container rowGap={2} size={12}>
                <AddressRowOne
                  control={control}
                  line1={watchLine1}
                  hasError={{ line1: addressErrors?.line1 && !watchLine1 }}
                  onLine1Change={newValue => setValue('address.line1', newValue)}
                  onBillingAddressChange={handleAddressChange}
                  onKeyDown={noop}
                />
              </Grid>

              <Grid contaner size={12}>
                <AddressRowTwo
                  control={control}
                  hasError={{
                    city: addressErrors?.city && !watchCity,
                    state: addressErrors?.state && !watchState,
                    zipCode: addressErrors?.zipCode && !watchZipCode,
                  }}
                  onKeyDown={noop}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Box width="100%" display="flex" sx={{ '& label': { color: 'text.primary' } }}>
                  <HHFormSwitchIconField control={control} type="AccountStatus" label={T.ACTIVE_CONTACT} name="active" />
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Box width="100%" display="flex" sx={{ justifyContent: { md: 'end' }, '& label': { color: 'text.primary' } }}>
                  <HHFormSwitchIconField control={control} type="PropertyOwner" label={T.PROPERTY_OWNER} name="propertyOwner" />
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 12,
                }}
              >
                <Box width="100%" display="flex" sx={{ justifyContent: { md: 'end' }, '& label': { color: 'text.primary' } }}>
                  <HHFormSwitchIconField
                    control={control}
                    type="Invoice"
                    label={T.BILLING_ADDRESS_OVERRIDE}
                    name="beneficialOwnerOverride"
                  />
                </Box>
              </Grid>
              <Grid size={12}>
                <Typography color="textSecondary">{T.ASSOCIATED_WITH}</Typography>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={<HHFormCheckboxField control={control} name="associatedWithLocation" />}
                  label={capitalize(T.SERVICE_LOCATION)}
                />
              </Grid>
              {watch('associatedWithLocation') && (
                <Grid size={12}>
                  <HHFormMultiSelectAutocompleteField
                    ListboxProps={{ style: { maxHeight: '14rem' } }}
                    control={control}
                    deprecatedLabel={false}
                    disableCloseOnSelect
                    fullWidth
                    getOptionLabel={option => get(option, 'label', '')}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    label={`${capitalize(T.SERVICE_LOCATION)}s`}
                    name="locations"
                    options={serviceLocations}
                    sx={{ '.MuiOutlinedInput-root': { pr: '100px!important' } }}
                  />
                </Grid>
              )}
            </Grid>
            {!isTabletOrMobile && (
              <Grid size="auto">
                <Divider orientation="vertical" />
              </Grid>
            )}
            <Grid
              sx={isTabletOrMobile ? {} : { overflowY: 'auto', maxHeight: 'calc(100vh - 270px)' }}
              size={{
                xs: 12,
                md: 'grow',
              }}
            >
              <Typography variant="subtitle2">{capitalize(T.CONTACT_METHODS)}</Typography>
              <Divider sx={{ mt: 0.5 }} />
              {contactInfos.length > 0 && (
                <FormProvider {...contactForm}>
                  <List disablePadding>
                    {contactInfos.map((contactMethod, index) => (
                      <>
                        <BeneficialOwnerContactInfo
                          index={index}
                          isExpanded={expandedContactMethods.includes(index)}
                          onExpandChange={open =>
                            setExpandedContactMethods(
                              open ? [index] : expandedContactMethods.filter(expandedMethod => expandedMethod !== index)
                            )
                          }
                          contactMethod={contactMethod}
                          onDelete={() => removeContactMethod(index)}
                        />
                        <Divider sx={{ mt: 1 }} />
                      </>
                    ))}
                  </List>
                </FormProvider>
              )}
              <Button sx={{ mt: 3 }} fullWidth variant="outlined" startIcon={<AddIcon />} onClick={handleAddContactMethod}>
                {capitalize(T.ADD_CONTACT_METHOD)}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <HHDialogActions>
          <CancelButton onClick={onClose} />
          <SaveButton loading={loading} onClick={handleSubmit(onSubmitHandler, onSubmitErrorHandler)} />
        </HHDialogActions>
      </HHBaseDialog>
    </>
  )
}

AddEditBeneficialOwnerDialog.propTypes = {
  isOpen: PropTypes.bool,
  accountId: PropTypes.string.isRequired,
  contact: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

export default AddEditBeneficialOwnerDialog
